import _ from "lodash";
import { parseISO, isMatch, parse } from "date-fns";

import {
  DateField as MuiDateField,
  DateFieldProps as MuiDateFieldProps,
} from "@samacare/design/core";

import {
  UseControllerProps,
  useFormContext,
  useFormState,
  FieldError,
} from "react-hook-form";

export function getValidDate(
  value: string | Date | undefined | null,
): Date | undefined {
  if (value instanceof Date) return value;
  if (!value || value === null) return undefined;

  if (isMatch(value, "yyyy/MM/dd"))
    return parse(value, "yyyy/MM/dd", new Date());

  if (isMatch(value, "yyyy-MM-dd"))
    return parse(value, "yyyy-MM-dd", new Date());

  if (isMatch(value, "MM / dd / yyyy"))
    return parse(value, "MM / dd / yyyy", new Date());

  if (isMatch(value, "MM/dd/yyyy"))
    return parse(value, "MM/dd/yyyy", new Date());

  return parseISO(value);
}

export type DateFieldProps = MuiDateFieldProps<Date> & UseControllerProps;

const DateField: React.FC<DateFieldProps> = ({
  name,
  rules = {},
  required = false,
  disabled = false,
  shouldUnregister = false,
  ...props
}) => {
  const { register, control, getValues, setValue, trigger } = useFormContext();
  const { errors } = useFormState({ control });

  const dateValue = getValidDate(getValues(name) as Date | string | undefined);

  const error = _.get(errors, name) as FieldError;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ref, onChange, onBlur, ...fields } = register(name, {
    ...rules,
    required,
    shouldUnregister,
    value: dateValue,
  });

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur) {
      props.onBlur(event);
    }
    await trigger(name);
  };

  return (
    <MuiDateField
      {...fields}
      id={`DateField-${name}`}
      inputProps={{
        "data-cy": _.camelCase(`field.${name}`),
      }}
      onChange={async (option) => setValue(name, option, { shouldDirty: true })}
      helperText={
        error && error.type === "required"
          ? `${props.label} required`
          : error?.message ?? ""
      }
      required={required}
      disabled={disabled}
      format="MM/dd/yyyy"
      value={dateValue}
      onBlur={handleBlur}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore How do we make this not complain.. can we add the error prop to the @samacare/DateField props?
      error={!!error}
      {...props}
    />
  );
};

export default DateField;
